import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const ToastCtx = createContext(null);

let idSeq = 0;

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const remove = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const show = useCallback(
        (toast) => {
            const id = ++idSeq;
            const t = { id, variant: "success", duration: 4000, ...toast };
            setToasts((prev) => [...prev, t]);
            if (t.duration > 0) {
                setTimeout(() => remove(id), t.duration);
            }
        },
        [remove]
    );

    const value = useMemo(() => ({ show, remove }), [show, remove]);

    return (
        <ToastCtx.Provider value={value}>
            {children}
            {/* Container */}
            <div className="fixed z-[60] top-3 right-3 sm:top-4 sm:right-4 space-y-2 sm:space-y-3 w-[92vw] max-w-sm">
                {toasts.map((t) => (
                    <Toast key={t.id} toast={t} onClose={() => remove(t.id)} />
                ))}
            </div>
        </ToastCtx.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastCtx);
    if (!ctx) throw new Error("useToast must be used within <ToastProvider />");
    return ctx;
}

function Toast({ toast, onClose }) {
    // Determine title/description from toast props
    // Support "message" as an alias for "description" or "title" depending on usage
    // The previous implementation used title and description.
    // If the user called show({ message: "..." }), we want to render that message.
    let { title, description, message } = toast;
    const variant = toast.variant || toast.type || "success";

    if (!title && !description && message) {
        // If only message is provided, treat it as description or title.
        // Let's treat it as description usually, but if there's no title, it might look better as title?
        // Let's use it as description generally, or title if it's short.
        // For consistency with typical "toast(message)" calls, usually it's the main text.
        // Let's assign it to description to allow for potentially multiline text.
        // But wait, the previous usage was show({ message: ... }).
        description = message;
    }

    const palette =
        {
            success: {
                bar: "bg-success-light dark:bg-success-dark",
                bg: "bg-card-light dark:bg-card-dark",
                textTitle: "text-foreground-light dark:text-foreground-dark",
                textDesc: "text-subtle-light dark:text-subtle-dark",
                icon: (
                    <svg
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                    >
                        <path
                            d="M5 13l4 4L19 7"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                ),
            },
            error: {
                bar: "bg-danger-light dark:bg-danger-dark",
                bg: "bg-card-light dark:bg-card-dark",
                textTitle: "text-foreground-light dark:text-foreground-dark",
                textDesc: "text-subtle-light dark:text-subtle-dark",
                icon: (
                    <svg
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                    >
                        <path d="M12 9v4m0 4h.01M4.93 4.93l14.14 14.14" strokeLinecap="round" />
                    </svg>
                ),
            },
            info: {
                bar: "bg-info-light dark:bg-info-dark",
                bg: "bg-card-light dark:bg-card-dark",
                textTitle: "text-foreground-light dark:text-foreground-dark",
                textDesc: "text-subtle-light dark:text-subtle-dark",
                icon: (
                    <svg
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                    >
                        <path
                            d="M13 16h-1v-4h-1M12 8h.01"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                ),
            },
        }[variant] || {};

    return (
        <div
            className={`relative overflow-hidden rounded-lg border border-border-light dark:border-border-dark shadow-lg ${palette.bg}`}
        >
            <div className={`absolute left-0 top-0 h-full w-1 ${palette.bar}`} />
            <div className="p-3 pl-4 pr-9 sm:p-4 sm:pl-5 sm:pr-10">
                <div className="flex items-start gap-2.5 sm:gap-3">
                    <div className="mt-0.5 text-foreground-light dark:text-foreground-dark">
                        {palette.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                        {title && (
                            <div
                                className={`text-xs sm:text-sm font-semibold ${palette.textTitle}`}
                            >
                                {title}
                            </div>
                        )}
                        {description && (
                            <div
                                className={`text-[11px] sm:text-xs mt-1 ${palette.textDesc}`}
                            >
                                {description}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 p-1 rounded hover:bg-border-light/50 dark:hover:bg-border-dark/50"
                        aria-label="Close"
                    >
                        <svg
                            className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-subtle-light dark:text-subtle-dark"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}